/**
 * RegisterPage.tsx
 * Page d'inscription modernisée avec Tailwind CSS
 */
import React, { useState } from 'react';
import { useAuthContext } from '../components/auth/AuthProvider';
import { useToast } from '../components/react/common/ToastProvider';
import { User, Mail, Lock, Loader2 } from 'lucide-react';

export interface RegisterPageProps {
  onBackToLogin?: () => void;
}

export function RegisterPage({ onBackToLogin }: RegisterPageProps): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { signUp } = useAuthContext();
  const { addToast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      addToast({
        type: 'error',
        message: 'Les mots de passe ne correspondent pas',
      });
      return;
    }

    if (password.length < 6) {
      addToast({
        type: 'error',
        message: 'Le mot de passe doit contenir au moins 6 caractères',
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await signUp(email, password, fullName);
      if (error) {
        addToast({
          type: 'error',
          message: error.message || "Erreur lors de l'inscription",
        });
      } else {
        addToast({
          type: 'success',
          message: 'Inscription réussie ! Vérifiez votre email pour confirmer votre compte.',
        });
        // Retourner à la page de connexion après inscription
        if (onBackToLogin) {
          setTimeout(() => {
            onBackToLogin();
          }, 2000);
        }
      }
    } catch (_error) {
      addToast({
        type: 'error',
        message: 'Une erreur inattendue est survenue',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-brand">
          <div className="auth-brand-mark" aria-hidden="true">
            SP
          </div>
          <div>
            <h1 className="auth-title">Créer votre compte</h1>
            <p className="auth-subtitle">Quelques infos et c'est parti</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="auth-field">
            <label htmlFor="fullName" className="auth-field-label">
              Nom complet
            </label>
            <div className="auth-input-group">
              <span className="auth-input-icon" aria-hidden="true">
                <User />
              </span>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                disabled={isLoading}
                placeholder="Jean Dupont"
                autoComplete="name"
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="email" className="auth-field-label">
              Email
            </label>
            <div className="auth-input-group">
              <span className="auth-input-icon" aria-hidden="true">
                <Mail />
              </span>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                disabled={isLoading}
                placeholder="votre@email.com"
                autoComplete="email"
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="password" className="auth-field-label">
              Mot de passe
            </label>
            <div className="auth-input-group">
              <span className="auth-input-icon" aria-hidden="true">
                <Lock />
              </span>
              <input
                id="password"
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                disabled={isLoading}
                placeholder="Au moins 6 caractères"
                autoComplete="new-password"
              />
            </div>
          </div>

          <div className="auth-field">
            <label htmlFor="confirmPassword" className="auth-field-label">
              Confirmer
            </label>
            <div className="auth-input-group">
              <span className="auth-input-icon" aria-hidden="true">
                <Lock />
              </span>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                required
                disabled={isLoading}
                placeholder="Re-tapez le mot de passe"
                autoComplete="new-password"
              />
            </div>
          </div>

          <button type="submit" disabled={isLoading} className="auth-btn-primary">
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Inscription…</span>
              </>
            ) : (
              <span>Créer mon compte</span>
            )}
          </button>
        </form>

        <p className="auth-footer">
          Déjà un compte ?{' '}
          <button type="button" onClick={onBackToLogin}>
            Se connecter
          </button>
        </p>
      </div>
    </div>
  );
}
